import React, { useEffect, useState } from 'react';
import {
  Button,
  CondensedSmall,
  Flashy,
  Form,
  Gutter,
  Icon,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  SectionHeader,
  useApi,
  DateUtil,
} from '@axiom/ui';
import { isLpUser } from '@axiom/utils';
import { CandidatesConst } from '@axiom/const';

import { CandidatePreferencesUtil } from '../../utils/candidate-preferences-util';
import { PreferredWeeklyHoursUtil } from '../../utils/preferred-weekly-hours-util';
import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { WorkFeedApi } from '../../api/work-feed-api';
import { useCandidateId } from '../../hooks/useCandidateId';
import {
  AvailabilityPreferencesSchema,
  type AvailabilityPreferences,
} from '../../schema/availability-preferences-schema';

import { PreferredWeeklyHoursEngagements } from './PreferredWeeklyHoursEngagements';
import { PreferredWeeklyHoursStatusMessage } from './PreferredWeeklyHoursStatusMessage';
import { PreferredWeeklyHoursSlider } from './PreferredWeeklyHoursSlider';

const {
  availabilityPreferencesUpdatedByTypes: { Talent },
} = CandidatesConst;

export const PreferredWeeklyHours = () => {
  const [upToDate, setUpToDate] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  const candidateId = useCandidateId();
  const [{ data: user }, { data: candidate }] = useApi(
    UserApi.readUser(),
    CandidateApi.readCandidate(candidateId)
  );

  const maxWeeklyHours = PreferredWeeklyHoursUtil.getMaxWeeklyHours({
    candidate,
  });

  const initialValues: Partial<AvailabilityPreferences> = {
    preferredWeeklyMaxHours: candidate.desiredWeeklyMaxHours ?? maxWeeklyHours,
  };

  useEffect(() => {
    const lpUser = isLpUser(user);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (formDirty && lpUser) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formDirty, user]);

  useEffect(() => {
    if (formDirty) {
      setUpToDate(false);
    }
  }, [formDirty]);

  const handleSubmit = async (values: AvailabilityPreferences) => {
    await CandidateApi.updateCandidate(candidateId, {
      desiredWeeklyMaxHours: values.preferredWeeklyMaxHours,
    });
    await CandidateApi.refreshCandidate(candidateId);
    WorkFeedApi.clearWorkFeed(candidateId);
    WorkFeedApi.clearWorkFeedSaved(candidateId);
    setUpToDate(true);
  };

  const lastUpdate = candidate.availabilityPreferencesUpdatedAt;
  const lastUpdateUserType = candidate.availabilityPreferencesUpdatedByType;
  const { calculatedDisplayName } = candidate;

  if (PreferredWeeklyHoursUtil.isOnLeave({ candidate })) {
    return (
      <div data-test="ON_LEAVE_MESSAGE">
        <ParagraphHeader name="ON_LEAVE_HEADER">
          We show you are currently on leave.
        </ParagraphHeader>
        <Gutter bottom="16px" />
        <Paragraph name="ON_LEAVE_TEXT">
          If that's not correct or you want to let Axiom know when you will be
          available for work, email your talent partner below.
        </Paragraph>
      </div>
    );
  }

  return (
    <Form
      name="availability-preferences-form"
      schema={AvailabilityPreferencesSchema}
      initialValues={initialValues}
      onSubmit={async (_changedData, _actions, values) => {
        await handleSubmit(values);
      }}
    >
      {({ values, fireSubmit, dirty, setValues }) => {
        if (dirty !== formDirty) {
          // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
          setTimeout(() => {
            setFormDirty(dirty);
          }, 0);
        }

        return (
          <>
            <PreferredWeeklyHoursEngagements name="PREFERRED_WEEKLY_HOURS_ENGAGEMENTS" />
            <SectionHeader name="PREFERRED_WEEKLY_HOURS_HEADER">
              Preferred weekly hours:{' '}
              <Flashy
                color="brandAxiomOrange"
                name="PREFERRED_WEEKLY_HOURS_VALUE"
              >
                {values.preferredWeeklyMaxHours}
              </Flashy>
            </SectionHeader>
            <Gutter bottom="16px" />

            <PreferredWeeklyHoursSlider
              name="PREFERRED_WEEKLY_HOURS_SLIDER"
              maxWeeklyHours={maxWeeklyHours}
              currentValue={values.preferredWeeklyMaxHours ?? 0}
              onValueChange={(newValue: number) => {
                void setValues({
                  ...values,
                  preferredWeeklyMaxHours: newValue,
                });
              }}
            />

            <PreferredWeeklyHoursStatusMessage
              name="PREFERRED_WEEKLY_HOURS_STATUS_MESSAGE"
              candidate={candidate}
            />

            <Layout position="right">
              <LayoutItem>
                {upToDate ? (
                  <Paragraph name="UP_TO_DATE_MESSAGE">
                    Up to date <Icon name="checkmark" />
                  </Paragraph>
                ) : (
                  <Button
                    name="PENDO-MY-WORK-SAVE-BUTTON"
                    variation="outline"
                    onClick={fireSubmit}
                    className="gtm-save-user-preferences"
                  >
                    {CandidatePreferencesUtil.canConfirmPreferences(lastUpdate)
                      ? 'Confirm'
                      : 'Save'}
                  </Button>
                )}
              </LayoutItem>
            </Layout>

            {lastUpdate ? (
              <Gutter top="16px">
                <CondensedSmall name="LAST_UPDATE_TEXT">
                  Last Updated: {DateUtil.displayRelativeTime(lastUpdate)} by{' '}
                  {lastUpdateUserType === Talent
                    ? calculatedDisplayName
                    : 'Axiom'}
                </CondensedSmall>
              </Gutter>
            ) : null}
          </>
        );
      }}
    </Form>
  );
};
